import { createRequestSaga, showError, showSuccess } from '@weave/alert-system';
import { all, call, delay, put, select, takeLatest } from 'redux-saga/effects';

import { VerticalEnum } from '../../../../models/vertical-enum';
import { getErrorMessage, getParsedErrorMessage } from '../../../axios';
import { history } from '../../../store/history';
import { ResolvedPromise } from '../../../store/store-utils';
import { clearCachedActions } from '../../cached-actions';
import { addNewUser } from '../../users';
import { selectCurrentLocationId, unsetCurrentLocationId } from '../current-location';
import {
  createLocationFailure,
  createLocationLoading,
  createLocationSuccess,
  ProvisionLocationRequestDTO,
  ProvisionLocationResponseDTO,
} from '../location-status';
import * as locationApi from './api';
import { selectLocation } from './locations.selectors';
import {
  AddLocationsV2Action,
  createDemoUserFromLocation,
  LocationActions,
  LocationModel,
  LocationsActionTypes,
  LocationsV2ActionTypes,
  LocationTypes,
  LocationV2,
  OfficeInfo,
  RemoveLocationAction,
  RemoveLocationV2Action,
  RequestCreateLocationAction,
  RequestSetLocationParentAction,
  RequestSetPhoneTenantID,
  RequestUpdateLocationAction,
  UpdateLocationV2Action,
} from './locations.types';

export const requestCreateLocation = (
  location: ProvisionLocationRequestDTO,
  createPhoneTenant: boolean
): RequestCreateLocationAction => ({
  type: LocationsActionTypes.RequestCreateLocation,
  payload: {
    location,
    createPhoneTenant,
  },
});
export const requestUpdateLocation = (
  locationId: string,
  location: Partial<LocationModel>,
  onFinish?: () => void
): RequestUpdateLocationAction => ({
  type: LocationsActionTypes.RequestUpdateLocation,
  payload: { locationId, location, onFinish },
});
export const requestSetLocationParent = (
  locationId: string,
  parentLocationId: string
): RequestSetLocationParentAction => ({
  type: LocationsActionTypes.RequestSetLocationParent,
  payload: { locationId, parentLocationId },
});
export const requestSetPhoneTenantID = (
  phoneTenanatId: string
): RequestSetPhoneTenantID => ({
  type: LocationsActionTypes.RequestSetPhoneTenantID,
  payload: { phoneTenanatId },
});
export const addLocations = (locations: LocationModel[]): LocationActions => ({
  type: LocationsActionTypes.AddLocations,
  payload: locations,
});
export const updateLocation = (locationId: string, location: Partial<LocationModel>) => ({
  type: LocationsActionTypes.UpdateLocation,
  payload: { locationId, location },
});
export const removeLocation = (locationId: string): LocationActions => ({
  type: LocationsActionTypes.RemoveLocation,
  payload: locationId,
});
export const addLocationsV2 = (locationsV2: LocationV2[]): AddLocationsV2Action => ({
  type: LocationsV2ActionTypes.AddLocationsV2,
  payload: locationsV2,
});
export const updateLocationV2 = (
  locationId: string,
  location: Partial<LocationV2>
): UpdateLocationV2Action => ({
  type: LocationsV2ActionTypes.UpdateLocationV2,
  payload: { locationId, location },
});
export const removeLocationV2 = (locationId: string): RemoveLocationV2Action => ({
  type: LocationsV2ActionTypes.RemoveLocationV2,
  payload: locationId,
});

// Fetch LocationV2 by ID action
export function* fetchAndAddLocationV2ById(locationId: string) {
  try {
    const locationData = yield call(locationApi.getLocationV2DetailById, locationId);
    if (locationData) {
      yield put(addLocationsV2([locationData]));
    }
    return locationData;
  } catch (error) {
    showError(`Failed to fetch location V2: ${getErrorMessage(error)}`);
    throw error;
  }
}

const LOCATION_CREATION_POLL_INTERVAL_MS = 3000;
const LOCATION_CREATION_MAX_POLL_ATTEMPTS = 20;

export function* pollDataLocationCreationStatus(
  provisionResponse: ProvisionLocationResponseDTO
) {
  const locationId = provisionResponse.locationId;

  if (!locationId) {
    return {
      ...provisionResponse,
      locationCreatedSuccessfully: false,
      locationProvisionError: '',
      creationStillProcessing: true,
    };
  }

  for (let attempt = 0; attempt < LOCATION_CREATION_MAX_POLL_ATTEMPTS; attempt++) {
    if (attempt > 0) {
      yield delay(LOCATION_CREATION_POLL_INTERVAL_MS);
    }

    try {
      const locationData: ResolvedPromise<
        ReturnType<typeof locationApi.getLocationV2DetailById>
      > = yield call(locationApi.getLocationV2DetailById, locationId);
      if (locationData?.locationId) {
        yield put(addLocationsV2([locationData]));
        return {
          ...provisionResponse,
          locationCreatedSuccessfully: true,
          phoneDataTenantCreatedSuccessfully: true,
          customizationFlagsSetSuccessfully: true,
          salesforceUpdatedSuccessfully: true,
          locationId,
          locationProvisionError: '',
        };
      }
    } catch {
      // Data-location may 404 briefly while async location creation finishes.
    }
  }

  return {
    ...provisionResponse,
    locationCreatedSuccessfully: false,
    locationProvisionError: '',
    creationStillProcessing: true,
  };
}

export const handleCreateLocation = createRequestSaga<RequestCreateLocationAction>({
  key: LocationsActionTypes.RequestCreateLocation,
  displayErrors: true,
  onError: (error) => `Location Creation Failure: ${error}`,
  saga: function* (action) {
    try {
      yield put(createLocationLoading());
      const { location: uncreatedLocation, createPhoneTenant } = action.payload;
      uncreatedLocation.provisionedFrom = 'WAM';
      let dto: typeof uncreatedLocation & ProvisionLocationResponseDTO;
      if (createPhoneTenant) {
        const provisionResponse: ResolvedPromise<
          ReturnType<typeof locationApi.newProvisionLocation>
        > = yield call(locationApi.newProvisionLocation, uncreatedLocation);
        const creationStatus: ProvisionLocationResponseDTO = yield call(
          pollDataLocationCreationStatus,
          {
            ...provisionResponse,
            phoneTenantCreationRequested: true,
          }
        );
        dto = { ...uncreatedLocation, ...creationStatus };
        yield put(createLocationSuccess(creationStatus));
        if (dto.locationType === LocationTypes.Demo && dto.salesforceProducts) {
          const user = createDemoUserFromLocation(dto);
          yield put(addNewUser(user));
        }
      } else {
        const locationId: ResolvedPromise<ReturnType<typeof locationApi.createLocation>> =
          yield call(locationApi.createLocation, uncreatedLocation);

        const newCreationResponse: ProvisionLocationResponseDTO = {
          locationCreatedSuccessfully: true,
          phoneDataTenantCreatedSuccessfully: true,
          customizationFlagsSetSuccessfully: true,
          salesforceUpdatedSuccessfully: true,
          locationId: locationId,
          tenantId: '',
          locationProvisionError: '',
          phoneTenantCreationRequested: false,
        };

        const creationStatus: ProvisionLocationResponseDTO = yield call(
          pollDataLocationCreationStatus,
          newCreationResponse
        );

        dto = { ...uncreatedLocation, ...creationStatus };
        yield put(createLocationSuccess(creationStatus));
      }
    } catch (error: any) {
      yield put(createLocationFailure());

      throw `Failed to create location: ${getParsedErrorMessage(error)}`;
    }
  },
});

export const handleUpdateLocation = createRequestSaga<RequestUpdateLocationAction>({
  key: LocationsActionTypes.RequestUpdateLocation,
  displayErrors: true,
  onError: (error) => getErrorMessage(error) || 'Failed to update location',
  saga: function* ({ payload }) {
    const { locationId, location: changes, onFinish } = payload;
    const prevLocation: ReturnType<ReturnType<typeof selectLocation>> = yield select(
      selectLocation(locationId)
    );
    if (!prevLocation) {
      yield put(showError(`Location ${locationId} not found`));
    }
    const location = {
      ...prevLocation,
      ...changes,
      ...(changes.VerticalID && { Vertical: VerticalEnum[changes.VerticalID] }),
    };
    yield call(locationApi.updateLocation, locationId, location);

    const officeInfo: OfficeInfo = {
      businessName: location.Name,
      businessPhone: location.Phone,
      businessEmail: location.Email,
      timezone: location.Timezone,
      industry: VerticalEnum[location.VerticalID] ?? 'Unknown',
    };

    yield put(updateLocation(locationId, location));
    yield call(locationApi.updateOfficeInfo, officeInfo);
    const locationV2 = yield call(locationApi.getLocationV2DetailById, locationId);
    yield put(updateLocationV2(locationId, locationV2));
    yield put(showSuccess('Location Updated Successfully'));
    if (onFinish) {
      onFinish();
    }
  },
});

export const handleSetLocationParent = function* (
  action: RequestSetLocationParentAction
) {
  try {
    const { locationId, parentLocationId } = action.payload;
    yield call(locationApi.setLocationParent, locationId, parentLocationId);

    yield put(updateLocation(locationId, { ParentID: parentLocationId }));
    yield put(showSuccess('Location Parentage Updated Successfully'));
  } catch (error: any) {
    yield put(showError(getErrorMessage(error)));
  }
};

export const handleSetPhoneTenantId = function* (action: RequestSetPhoneTenantID) {
  try {
    const locationId: ReturnType<typeof selectCurrentLocationId> = yield select(
      selectCurrentLocationId
    );
    const { phoneTenanatId } = action.payload;
    yield call(locationApi.setPhoneTenantId, locationId, phoneTenanatId);
    yield put(updateLocation(locationId, { PhoneTenantID: phoneTenanatId }));
    yield put(showSuccess('Phone tenant id updated Successfully'));
  } catch (error: any) {
    yield put(showError(getErrorMessage(error)));
  }
};

export const handleRemoveLocation = function* (action: RemoveLocationAction) {
  const locationId: ReturnType<typeof selectCurrentLocationId> = yield select(
    selectCurrentLocationId
  );
  // Todo: eventually we may not want to remove these cached actions when removing a location
  // Will revisit this once our cached actions season a little longer
  yield put(clearCachedActions(action.payload));

  // Also remove the location from V2 state
  yield put(removeLocationV2(action.payload));

  if (locationId === action.payload) {
    yield put(unsetCurrentLocationId());
    history.push('/');
  }
};

export const locationsSaga = function* () {
  yield all([
    yield takeLatest(LocationsActionTypes.RequestCreateLocation, handleCreateLocation),
    yield takeLatest(LocationsActionTypes.RequestUpdateLocation, handleUpdateLocation),
    yield takeLatest(
      LocationsActionTypes.RequestSetLocationParent,
      handleSetLocationParent
    ),
    yield takeLatest(
      LocationsActionTypes.RequestSetPhoneTenantID,
      handleSetPhoneTenantId
    ),
    yield takeLatest(LocationsActionTypes.RemoveLocation, handleRemoveLocation),
  ]);
};
